import { Phone, Mail, MapPin, MessageCircle, Download } from "lucide-react";
import { CONTACT } from "@/content/site";
import { Reveal } from "@/components/Reveal";
import { VoiceMessage } from "@/components/VoiceMessage";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const ContactSection = () => {
  return (
    <section id="kontakt" data-testid="contact-section" className="py-16 md:py-32 border-t border-[#F0F0F0]">
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 grid lg:grid-cols-2 gap-10 lg:gap-24">
        <Reveal>
          <p className="flex items-center gap-4 text-xs uppercase tracking-[0.25em] text-[#737373] font-medium">
            <span className="w-10 h-[1px] bg-[#B69A58]" aria-hidden="true" />
            Kontakt
          </p>
          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl tracking-tight leading-[1.15] text-[#252525] mt-4 md:mt-6 max-w-2xl">
            Ozvěte se — stačí WhatsApp nebo telefon
          </h2>
          <p className="text-base md:text-lg text-[#4A4A4A] leading-relaxed mt-5 md:mt-8 max-w-lg">
            Napište mi nebo zavolejte. Nezávaznou konzultaci můžeme domluvit osobně i online.
          </p>

          <div className="flex flex-row gap-3 mt-6 md:mt-10">
            <a
              href={CONTACT.whatsappHref}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="contact-whatsapp-button"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border border-[#B69A58] bg-white text-[#252525] hover:bg-[#B69A58] hover:text-white transition-colors duration-300 px-4 py-3.5 sm:px-8 sm:py-4 text-[11px] sm:text-xs tracking-[0.12em] sm:tracking-widest uppercase font-medium whitespace-nowrap"
            >
              <MessageCircle className="h-4 w-4 text-[#B69A58]" strokeWidth={1.5} aria-hidden="true" />
              Napsat na WhatsApp
            </a>
            <a
              href={CONTACT.phoneHref}
              data-testid="contact-call-button"
              className="flex-1 sm:flex-none inline-flex items-center justify-center gap-2 border border-[#E5E5E5] text-[#252525] hover:border-[#252525] transition-colors duration-300 px-4 py-3.5 sm:px-8 sm:py-4 text-[11px] sm:text-xs tracking-[0.12em] sm:tracking-widest uppercase font-medium whitespace-nowrap"
            >
              <Phone className="h-4 w-4" strokeWidth={1.5} aria-hidden="true" />
              Zavolat
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="border border-[#F0F0F0] bg-white p-6 md:p-10 h-full flex flex-col justify-center" data-testid="contact-details-card">
            <div className="space-y-4 md:space-y-5">
              <a href={CONTACT.phoneHref} data-testid="contact-phone" className="flex items-center gap-4 group w-fit">
                <Phone className="h-5 w-5 text-[#B69A58]" strokeWidth={1.5} aria-hidden="true" />
                <span className="text-base text-[#252525] group-hover:text-[#B69A58] transition-colors duration-300">{CONTACT.phone}</span>
              </a>
              <a href={`mailto:${CONTACT.email}`} data-testid="contact-email" className="flex items-center gap-4 group w-fit">
                <Mail className="h-5 w-5 text-[#B69A58]" strokeWidth={1.5} aria-hidden="true" />
                <span className="text-base text-[#252525] group-hover:text-[#B69A58] transition-colors duration-300">{CONTACT.email}</span>
              </a>
              <p data-testid="contact-address" className="flex items-start gap-4">
                <MapPin className="h-5 w-5 text-[#B69A58] shrink-0 mt-0.5" strokeWidth={1.5} aria-hidden="true" />
                <span className="text-base text-[#252525]">{CONTACT.address}</span>
              </p>
            </div>
            <p className="text-sm text-[#737373] leading-relaxed mt-6 pt-6 md:mt-8 md:pt-8 border-t border-[#F0F0F0]" data-testid="contact-area-note">
              {CONTACT.area}
            </p>
          </div>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-20 mt-12 md:mt-28 pt-10 md:pt-16 border-t border-[#F0F0F0] grid md:grid-cols-2 gap-10 md:gap-16">
        <Reveal>
          <VoiceMessage />
        </Reveal>

        <Reveal delay={0.05}>
          <h3 className="font-display text-xl md:text-3xl text-[#252525]">Vizitka do telefonu</h3>
          <p className="hidden md:block text-sm md:text-base text-[#4A4A4A] leading-relaxed mt-4 max-w-md">
            Naskenujte QR kód mobilem a telefon vám nabídne uložit můj kontakt přímo do adresáře.
          </p>
          <div className="flex items-start gap-8 mt-5 md:mt-8">
            <img
              src="/images/qr-vizitka.png"
              alt="QR kód s kontaktem Daniela Pekaře"
              width={396}
              height={396}
              loading="lazy"
              data-testid="vcard-qr"
              className="hidden md:block w-36 h-36 border border-[#F0F0F0] p-2"
            />
            <a
              href={`${API}/vizitka.vcf`}
              download="Daniel-Pekar.vcf"
              data-testid="vcard-download"
              className="inline-flex items-center gap-2 border border-[#E5E5E5] text-[#252525] hover:border-[#252525] transition-colors duration-300 px-5 py-3.5 md:px-6 md:py-4 text-xs uppercase tracking-widest font-medium md:mt-2"
            >
              <Download className="h-4 w-4 text-[#B69A58]" strokeWidth={1.5} aria-hidden="true" />
              Uložit kontakt do telefonu
            </a>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
